import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Table, 
  Plus, 
  Trash,
  Upload,
  Download,
  Warning
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { API_URL, formatCurrency } from '../../lib/utils';

const plans = ['A', 'B', 'C', 'D'];
const relations = ['Employee', 'Spouse', 'Child', 'Parent'];
const ageBands = ['0-5', '6-10', '11-18', '19-25', '26-35', '36-45', '46-50', '51-55', '56-58', '59-65', '66-75', '76+'];
const maternityAgeBands = ['18-22', '23-34', '35-45'];

export default function AdminRates() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [baseRates, setBaseRates] = useState([]);
  const [maternityRates, setMaternityRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [newBaseRate, setNewBaseRate] = useState({
    plan: 'A',
    relation: 'Employee',
    age_band: '26-35',
    hospital_limit: 1000000,
    room_limit: 15000,
    annual_rate: 20000,
  });
  
  const [newMaternityRate, setNewMaternityRate] = useState({
    plan: 'A',
    maternity_age_band: '23-34',
    maternity_normal_limit: 50000,
    maternity_c_section_limit: 100000,
    annual_rate: 10000,
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchRates();
    }
  }, [user]);

  const fetchRates = async () => {
    try {
      const [baseRes, matRes] = await Promise.all([
        fetch(`${API_URL}/admin/base-rates`, { credentials: 'include' }),
        fetch(`${API_URL}/admin/maternity-rates`, { credentials: 'include' }),
      ]);
      
      if (baseRes.ok) setBaseRates(await baseRes.json());
      if (matRes.ok) setMaternityRates(await matRes.json());
    } catch (err) {
      console.error('Failed to fetch rates:', err);
    } finally {
      setLoading(false);
    }
  };

  const addBaseRate = async () => {
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_URL}/admin/base-rates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newBaseRate),
      });
      
      if (!response.ok) throw new Error('Failed to add rate');
      
      setSuccess('Base rate added successfully');
      fetchRates();
    } catch (err) {
      setError(err.message);
    }
  };

  const addMaternityRate = async () => {
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${API_URL}/admin/maternity-rates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newMaternityRate),
      });
      
      if (!response.ok) throw new Error('Failed to add rate');
      
      setSuccess('Maternity rate added successfully');
      fetchRates();
    } catch (err) {
      setError(err.message);
    }
  };

  const clearAllRates = async (type) => {
    if (!window.confirm(`Are you sure you want to delete all ${type} rates? This cannot be undone.`)) return;
    
    setError('');
    setSuccess('');
    try {
      const endpoint = type === 'base' ? '/admin/base-rates' : '/admin/maternity-rates';
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (!response.ok) throw new Error('Failed to delete rates');
      
      setSuccess(`All ${type} rates deleted`);
      fetchRates();
    } catch (err) {
      setError(err.message);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0048ff] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      {/* Header */}
      <header className="bg-[#0A1128] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors">
                <ArrowLeft size={18} />
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-[#0048ff]/10 flex items-center justify-center">
              <Table size={24} className="text-[#0048ff]" weight="duotone" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-[#11111f]">Rate Matrix Management</h1>
              <p className="text-[#11111f]/60">Manage insurance premium rate tables</p>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 flex items-center gap-2">
              <Warning size={18} />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-600">
              {success}
            </div>
          )}

          <Tabs defaultValue="base" className="space-y-6">
            <TabsList className="bg-white border border-[#11111f]/10">
              <TabsTrigger value="base">Base Rates ({baseRates.length})</TabsTrigger>
              <TabsTrigger value="maternity">Maternity Rates ({maternityRates.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="base">
              {/* Add Base Rate Form */}
              <div className="mentor-card mb-6">
                <h3 className="font-semibold text-[#11111f] mb-4">Add Base Rate</h3>
                <div className="grid md:grid-cols-6 gap-4">
                  <div>
                    <Label className="text-xs">Plan</Label>
                    <Select value={newBaseRate.plan} onValueChange={(v) => setNewBaseRate({...newBaseRate, plan: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {plans.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Relation</Label>
                    <Select value={newBaseRate.relation} onValueChange={(v) => setNewBaseRate({...newBaseRate, relation: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {relations.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Age Band</Label>
                    <Select value={newBaseRate.age_band} onValueChange={(v) => setNewBaseRate({...newBaseRate, age_band: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ageBands.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Hospital Limit</Label>
                    <Input 
                      type="number" 
                      value={newBaseRate.hospital_limit} 
                      onChange={(e) => setNewBaseRate({...newBaseRate, hospital_limit: parseInt(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Room Limit</Label>
                    <Input 
                      type="number" 
                      value={newBaseRate.room_limit} 
                      onChange={(e) => setNewBaseRate({...newBaseRate, room_limit: parseInt(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Annual Rate</Label>
                    <Input 
                      type="number" 
                      value={newBaseRate.annual_rate} 
                      onChange={(e) => setNewBaseRate({...newBaseRate, annual_rate: parseInt(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button onClick={addBaseRate} className="bg-[#0048ff]">
                    <Plus size={16} className="mr-2" /> Add Rate
                  </Button>
                  <Button variant="outline" onClick={() => clearAllRates('base')} className="text-red-500 border-red-200 hover:bg-red-50">
                    <Trash size={16} className="mr-2" /> Clear All
                  </Button>
                </div>
              </div>

              {/* Base Rates Table */}
              <div className="mentor-card overflow-x-auto">
                {loading ? (
                  <div className="py-8 text-center">
                    <div className="animate-spin w-6 h-6 border-2 border-[#0048ff] border-t-transparent rounded-full mx-auto" />
                  </div>
                ) : baseRates.length === 0 ? (
                  <div className="py-8 text-center text-[#11111f]/60">No base rates configured</div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#11111f]/10">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[#11111f]/60">Plan</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[#11111f]/60">Relation</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[#11111f]/60">Age Band</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[#11111f]/60">Hospital Limit</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[#11111f]/60">Room Limit</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[#11111f]/60">Annual Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {baseRates.slice(0, 50).map((rate, idx) => (
                        <tr key={idx} className="border-b border-[#11111f]/5">
                          <td className="py-2 px-3 text-sm">{rate.plan}</td>
                          <td className="py-2 px-3 text-sm">{rate.relation}</td>
                          <td className="py-2 px-3 text-sm">{rate.age_band}</td>
                          <td className="py-2 px-3 text-sm">{formatCurrency(rate.hospital_limit)}</td>
                          <td className="py-2 px-3 text-sm">{formatCurrency(rate.room_limit)}</td>
                          <td className="py-2 px-3 text-sm font-medium text-[#0048ff]">{formatCurrency(rate.annual_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {baseRates.length > 50 && (
                  <p className="text-center text-sm text-[#11111f]/60 py-4">Showing 50 of {baseRates.length} rates</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="maternity">
              {/* Add Maternity Rate Form */}
              <div className="mentor-card mb-6">
                <h3 className="font-semibold text-[#11111f] mb-4">Add Maternity Rate</h3>
                <div className="grid md:grid-cols-5 gap-4">
                  <div>
                    <Label className="text-xs">Plan</Label>
                    <Select value={newMaternityRate.plan} onValueChange={(v) => setNewMaternityRate({...newMaternityRate, plan: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {plans.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Age Band</Label>
                    <Select value={newMaternityRate.maternity_age_band} onValueChange={(v) => setNewMaternityRate({...newMaternityRate, maternity_age_band: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {maternityAgeBands.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Normal Limit</Label>
                    <Input 
                      type="number" 
                      value={newMaternityRate.maternity_normal_limit} 
                      onChange={(e) => setNewMaternityRate({...newMaternityRate, maternity_normal_limit: parseInt(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">C-Section Limit</Label>
                    <Input 
                      type="number" 
                      value={newMaternityRate.maternity_c_section_limit} 
                      onChange={(e) => setNewMaternityRate({...newMaternityRate, maternity_c_section_limit: parseInt(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Annual Rate</Label>
                    <Input 
                      type="number" 
                      value={newMaternityRate.annual_rate} 
                      onChange={(e) => setNewMaternityRate({...newMaternityRate, annual_rate: parseInt(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button onClick={addMaternityRate} className="bg-[#0048ff]">
                    <Plus size={16} className="mr-2" /> Add Rate
                  </Button>
                  <Button variant="outline" onClick={() => clearAllRates('maternity')} className="text-red-500 border-red-200 hover:bg-red-50">
                    <Trash size={16} className="mr-2" /> Clear All
                  </Button>
                </div>
              </div>

              {/* Maternity Rates Table */}
              <div className="mentor-card overflow-x-auto">
                {loading ? (
                  <div className="py-8 text-center">
                    <div className="animate-spin w-6 h-6 border-2 border-[#0048ff] border-t-transparent rounded-full mx-auto" />
                  </div>
                ) : maternityRates.length === 0 ? (
                  <div className="py-8 text-center text-[#11111f]/60">No maternity rates configured</div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#11111f]/10">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[#11111f]/60">Plan</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[#11111f]/60">Age Band</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[#11111f]/60">Normal Limit</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[#11111f]/60">C-Section Limit</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-[#11111f]/60">Annual Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {maternityRates.map((rate, idx) => (
                        <tr key={idx} className="border-b border-[#11111f]/5">
                          <td className="py-2 px-3 text-sm">{rate.plan}</td>
                          <td className="py-2 px-3 text-sm">{rate.maternity_age_band}</td>
                          <td className="py-2 px-3 text-sm">{formatCurrency(rate.maternity_normal_limit)}</td>
                          <td className="py-2 px-3 text-sm">{formatCurrency(rate.maternity_c_section_limit)}</td>
                          <td className="py-2 px-3 text-sm font-medium text-[#0048ff]">{formatCurrency(rate.annual_rate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>
    </div>
  );
}
